import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../Utils/toastContext';
import {
  clearCardCurrencyName,
  setPendingTraining,
} from '../../Redux/store/userSlice';
import useAutoLogout from '../custom-logout/custom-logout';
import SessionWarningModal from '../custom-logout/session-timeout-popup';
import {
  clearApplicationTypeShareHolder,
  clearApplicationType,
} from '../../Redux/store/accountTypeSlice';
import {
  clearOnboardingCustomer,
  clearStepStatus,
  clearCurrentStep,
  clearRejectedFields,
  clearStepStatusShareholder,
} from '../../Redux/store/onboardingFormSlice';
import {
  clearIsBusiness,
  clearRoleName,
  clearUserStatus,
  setSurveyStatus,
  setUserStatus,
  setAuthInitialized,
  setIsBack,
} from '../../Redux/store/userSlice';
import ContactUs from '../../Pages/Contact-Us/contact-us';
import { setEDDStatus, setRoleName } from '../../Redux/store/userSlice';
import { User, Shield, Briefcase, CheckCircle2 } from 'lucide-react';
import { debounce } from 'lodash';
import { formatDateTimeUTC } from '../../Constants/helper-functions';

const roles = [
  {
    key: 'agent',
    id: 7,
    label: 'Team Member',
    icon: User,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  {
    key: 'agency',
    label: 'Business Owner',
    id: 5,
    icon: Briefcase,
    bg: 'bg-blue-50',
    color: 'text-blue-600',
  },
  {
    key: 'agency_compliance',
    label: 'Compliance Officer',
    icon: Shield,
    id: 6,
    bg: 'bg-emerald-50',
    color: 'text-emerald-600',
  },
  {
    key: 'customer',
    label: 'Customer',
    icon: User,
    id: 8,
    bg: 'bg-purple-50',
    color: 'text-purple-600',
  },
];

const OnboardingHeader = () => {
  const dispatch = useDispatch();
  const roleName = useSelector((state) => state.user.roleName);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const logoutDropdown = useRef(null);
  const { showToast } = useToast();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const username = useSelector((state) => state.user.username);
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [selectedItem, setSelectedItem] = useState(0);
  const [readItems, setReadItems] = useState({});
  const [open, setOpen] = useState(false);
  const notificationRef = useRef(null);
  const [page, setPage] = useState(1);
  const handleNotifications = () => setOpen(!open);
  const notificationsDropdownRef = useRef(null);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showLoader, setShowLoader] = useState(false);

  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const availableRoles = useSelector((state) => state.user.availableRoles);
  console.log(' avila role' + availableRoles);
  const previousNotificationSeen = useRef(null);

  const availableRoleKeys = availableRoles?.map((r) => r.name);
  const filteredRoles = roles.filter((r) => availableRoleKeys.includes(r.key));
  const activeRole = filteredRoles.find((r) => r.key === roleName);
  const ActiveIcon = activeRole?.icon;
  const handleOpen = () => {
    previousNotificationSeen.current = localStorage.getItem(
      'notification_last_seen',
    );

    markAllAsRead();

    setTimeout(() => {
      localStorage.setItem('notification_last_seen', new Date().toISOString());
    }, 2000);
  };
  const parseNotificationDate = (date) => {
    if (!date) return null;

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      console.log('Invalid notification date:', date);
      return null;
    }

    return parsedDate;
  };
  const lastOpened = localStorage.getItem('notification_last_seen');
  const isNew = (item) => {
    if (!previousNotificationSeen.current) return false;

    const itemDate = parseNotificationDate(item.created_at);

    return itemDate && itemDate > new Date(previousNotificationSeen.current);
  };
  const NotificationItem = ({ item, isNew }) => (
    <div
      className={`group relative p-3 rounded-xl transition flex gap-3 ${
        isNew ? 'bg-blue-50 border border-blue-100' : 'bg-white border'
      } hover:bg-gray-50`}
    >
      {/* LEFT INDICATOR */}
      {isNew && (
        <div className="w-1 rounded-full bg-blue-600 absolute left-0 top-2 bottom-2"></div>
      )}

      {/* DOT */}
      <div className="mt-1">
        {isNew ? (
          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
        ) : (
          <div className="w-2 h-2 bg-transparent"></div>
        )}
      </div>

      {/* CONTENT */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <p className="text-sm font-medium text-gray-900 break-words min-w-0">
            {item.title}
          </p>

          {isNew && (
            <span className="text-[10px] font-semibold text-blue-600 bg-blue-100 px-2 py-[2px] rounded-full">
              New
            </span>
          )}
        </div>

        <p className="text-xs text-gray-600 mt-0.5 break-words">
          {item.message}
        </p>

        <p className="text-[10px] text-gray-400 mt-1">
          {formatDateTimeUTC(item.created_at)}
        </p>
      </div>

      {/* ACTIONS */}
      <div className="flex flex-col gap-1 text-[10px] shrink-0">
        <button
          onClick={() => handleDeleteNotification(item.id)}
          className="text-red-500 hover:text-red-700 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  );
  const notifications = historyData.items || [];
  const unreadCount = historyData.unread_count || 0;
  const newItems = notifications.filter(isNew);
  const oldItems = notifications.filter((item) => !isNew(item));
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowLoader(true), 200);
    } else {
      setShowLoader(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);
  const postSwitchAPI = async (roleid) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/auth/acting-role/exchange`;
    const bodyData = {
      role_id: roleid,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleUpdateSwtich = useCallback(
    debounce(async (e, rolename, roleid) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await postSwitchAPI(roleid);
        if (response.status === 200) {
          localStorage.setItem('jwtToken', response.data.access_token);
          localStorage.setItem('refreshJwtToken', response.data.refresh_token);
          proceedAfterLogin(response, rolename);
        }
      } catch (error) {
        toastMessage = { message: error.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [], // Dependencies
  );
  const markReadAllAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/notifications/all/read`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const proceedAfterLogin = async (response, roleName) => {
    dispatch(setRoleName(roleName));
    // 👉 continue your existing navigation logic here
    localStorage.setItem('lastActivity', Date.now().toString());
    dispatch(setAuthInitialized(false));

    setLoading(false);
    getProfilePicture();
    navigate('/');
    // const profileData = await getProfilePicture(response.data.access_token);
    // let haspendingTrining = false;
    // if (roleName === "customer") {
    //   dispatch(setEDDStatus(profileData.has_pending_edd));
    // }
    // if (roleName === "agency" || roleName === "agency_compliance") {
    //   dispatch(setSurveyStatus(profileData.has_pending_survey));
    // }
    // if (roleName === "agent" || roleName === "agency_compliance") {
    //   dispatch(setPendingTraining(profileData.has_pending_training));
    // }
    // dispatch(setAuthInitialized(true));
    //  sessionStorage.removeItem("redirectAfterLogin");
    //   navigate("/");
  };
  const markReadAPI = async (itemId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/notifications/${itemId}/read`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'PATCH',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const markAllAsRead = async () => {
    let toastMessage = null;
    try {
      const response = await markReadAllAPI();
      if (response.status == 200) {
        getnotifications(1);
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };
  const markDeleteAPI = async (itemId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/notifications/${itemId}`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleDeleteNotification = async (itemId) => {
    // setReadItems((prev) => ({
    //   ...prev,
    //   [itemId]: true, // mark specific item as read
    // }));
    let toastMessage = null;
    try {
      const response = await markDeleteAPI(itemId);
      if (response.status == 200) {
        getnotifications(1);
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };
  const handlemarkNotification = async (itemId) => {
    // setReadItems((prev) => ({
    //   ...prev,
    //   [itemId]: true, // mark specific item as read
    // }));
    let toastMessage = null;
    try {
      const response = await markReadAPI(itemId);
      if (response.status == 200) {
        console.log('marked as read' + itemId);
        getnotifications(1);
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollTop + clientHeight >= scrollHeight - 20 && !loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      getnotifications(nextPage);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Profile dropdown
      if (
        logoutDropdown.current &&
        !logoutDropdown.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }

      // Notifications dropdown
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleMarkAsRead = (itemId) => {
    setReadItems((prev) => ({
      ...prev,
      [itemId]: true, // mark specific item as read
    }));
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const PRESERVE_KEYS = ['redirectAfterLogin'];
  const clearAllSession = () => {
    const preserved = {};

    PRESERVE_KEYS.forEach((key) => {
      preserved[key] = sessionStorage.getItem(key) || localStorage.getItem(key);
    });

    localStorage.clear();
    sessionStorage.clear();

    PRESERVE_KEYS.forEach((key) => {
      if (preserved[key]) {
        sessionStorage.setItem(key, preserved[key]);
      }
    });
  };

  const clearOnFreshLoad = () => {
    const hasToken = localStorage.getItem('jwtToken');

    // If no token → treat as fresh session (new tab / expiry)
    if (!hasToken) {
      localStorage.clear();
    }
  };
  const handleLogout = () => {
    clearAllSession();

    dispatch(clearApplicationType());
    dispatch(clearApplicationTypeShareHolder());
    dispatch(clearCardCurrencyName());
    dispatch(clearApplicationType());
    dispatch(clearApplicationTypeShareHolder());
    dispatch(clearCardCurrencyName());
    dispatch(clearIsBusiness());
    dispatch(clearOnboardingCustomer());
    dispatch(clearRoleName());
    dispatch(clearUserStatus());
    dispatch(clearCurrentStep());
    dispatch(clearRejectedFields());
    dispatch(clearStepStatus());
    window.location.href = '/login';
  };

  const { timeLeft, isWarning, stayConnected } = useAutoLogout(handleLogout, {
    inactivityLimit: 10 * 60 * 1000, // 10 minutes
    warningThreshold: 60 * 1000, // 60 seconds show warning
  });
  const menuItemspopUp = [
    {
      id: 2,
      label: 'Logout',
      defaultIcon: '/logout.svg',
      selectedIcon: '/logout-white.svg',
    },
  ];
  const handleChangeMenu = () => {
    // toggleMenu();
    sessionStorage.removeItem('redirectAfterLogin');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('jwtToken');
    dispatch(clearApplicationType());
    dispatch(clearApplicationTypeShareHolder());
    dispatch(clearApplicationType());
    dispatch(clearApplicationTypeShareHolder());
    dispatch(clearCardCurrencyName());
    dispatch(clearIsBusiness());
    dispatch(clearOnboardingCustomer());
    dispatch(clearRoleName());
    dispatch(clearUserStatus());
    dispatch(clearCurrentStep());
    dispatch(clearRejectedFields());
    dispatch(clearStepStatus());
    navigate('/login');
    setIsDropdownOpen(false);
  };

  const toggleDropdown = (event) => {
    event.stopPropagation(); // Prevent click from propagating to the document listener
    setIsDropdownOpen((prev) => !prev);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        logoutDropdown.current &&
        !logoutDropdown.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        notificationsDropdownRef.current &&
        !notificationsDropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const getProfilePicture = async () => {
    try {
      const response = await GetAPIcalls('/api/v1/users/me');

      if (response.status == 200) {
        const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
        dispatch(setAuthInitialized(false));
        if (response.data.roles[0].name === 'customer') {
          dispatch(setEDDStatus(response.data.has_pending_edd));
        }
        if (
          response.data.roles[0].name === 'agency' ||
          response.data.roles[0].name === 'agency_compliance'
        ) {
          dispatch(setSurveyStatus(response.data.has_pending_survey));
        }
        if (
          response.data.roles[0].name === 'agent' ||
          response.data.roles[0].name === 'agency_compliance'
        ) {
          dispatch(setPendingTraining(response.data.has_pending_training));
        }

        dispatch(setUserStatus(response.data.status.label));
        dispatch(setAuthInitialized(true));
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getnotifications = async (pageNumber = 1) => {
    if (loading) return;

    setLoading(true);

    try {
      console.log('Fetching notifications for page:', pageNumber);
      const response = await GetAPIcalls(
        `/api/v1/notifications?page=${pageNumber}&limit=10`,
      );
      console.log('after Fetching notifications for page:', pageNumber);

      if (response.status === 200) {
        const newItems = response.data?.items || [];

        setHistoryData((prev) => {
          // First page → replace
          if (pageNumber === 1) {
            return response.data;
          }

          // Next pages → merge correctly
          return {
            ...prev,
            items: [...(prev.items || []), ...newItems],
          };
        });

        // Stop if no more data
        if (newItems.length < 10) {
          setHasMore(false);
        }
      } else if (response.status === 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getProfilePicture();
    getnotifications(1);
  }, []);
  return (
    <header className="pt-3 sm:pt-4 px-3 sm:px-4 flex flex-wrap items-center justify-between gap-y-3 py-0 w-full max-w-full">
      <div className="w-full md:w-auto flex flex-row items-center justify-between md:justify-start gap-3 min-w-0">
        <img
          src="/Ratified-Logo-Main.svg"
          alt="Ratified Logo"
          className="h-8 sm:h-9 w-auto max-w-[160px] sm:max-w-none shrink-0"
        />
        {/* Back Button */}

        {/* Mobile Center Message Toggle (optional hamburger or info icon) */}
        <div className="flex flex-wrap items-center gap-3 md:ml-6 lg:ml-12 shrink-0">
          {/* Back Button */}
          {location.pathname.startsWith('/onboarding-shareholder') && (
            <button
              className="flex items-center space-x-2 bg-white px-3 py-3 h-11 rounded-xl shadow-md"
              onClick={() => {
                dispatch(clearStepStatus());
                dispatch(clearStepStatusShareholder());
                //   dispatch(setIsBack(true));
                navigate(location.state?.from || '/onboarding');
              }}
            >
              <img src="/arrow-left.svg" alt="Back" className="w-4 h-4" />
              <span className="text-sm lg:inline hidden font-medium">Back</span>
            </button>
          )}
        </div>
      </div>

      {/* Right: Dropdowns (stacked on small screens, horizontal on md+) */}
      <div className="w-full md:w-auto flex flex-row items-center justify-end md:justify-start gap-2 md:gap-4 mt-0 min-w-0">
        {/* Profile & Notifications */}
        <div
          ref={logoutDropdown}
          className="relative flex items-center justify-between sm:justify-end gap-2 sm:gap-3 md:gap-4 w-full md:w-auto min-w-0"
        >
          {/* ROLE SWITCHER */}
          <div className="relative">
            {/* BUTTON */}
            <button
              onClick={() => setIsRoleOpen((prev) => !prev)}
              className="
      group flex items-center justify-between gap-2 sm:gap-4
      px-2 sm:px-4 py-2 h-11 sm:h-14 lg:h-16
      w-auto sm:min-w-[180px] lg:min-w-[220px] max-w-[60vw] sm:max-w-none
      bg-white rounded-xl border border-gray-100
      shadow-sm hover:shadow-md
      transition-all duration-200
    "
            >
              {/* LEFT SIDE */}
              <div className="flex items-center gap-2 sm:gap-3 py-2 sm:py-4 min-w-0">
                {/* ICON */}
                <div className={`p-2 rounded-lg shrink-0 ${activeRole?.bg}`}>
                  {ActiveIcon && (
                    <ActiveIcon className={`w-4 h-4 ${activeRole?.color}`} />
                  )}
                </div>

                {/* TEXT BLOCK */}
                <div className="hidden sm:flex flex-col leading-tight min-w-0">
                  {/* NAME (PRIMARY) */}
                  <p className="text-sm text-left font-medium text-black mt-1 break-words line-clamp-2 max-w-[140px] md:max-w-[200px] xl:max-w-none">
                    {username}
                  </p>

                  {/* ROLE LABEL */}
                  <span className="text-[11px] text-left text-gray-400 tracking-wide">
                    Your Role
                  </span>

                  {/* ROLE VALUE */}
                  <span className="text-xs text-left font-medium text-[#375AE7]">
                    {activeRole?.label}
                  </span>
                </div>
              </div>

              {/* RIGHT SIDE (STRONGER DROPDOWN INDICATOR) */}
              {/* {filteredRoles.length > 1 && (
                <div className="flex text-xs items-center gap-2">
                  <div
                    className={`
          flex items-center justify-center
          w-7 h-7 rounded-lg
          bg-gray-600
          transition-all duration-200
        `}
                  >
                    <img
                      src="/chevron-down.svg"
                      className={`
            w-4 h-4 text-gray-500
            transition-transform duration-300
            ${isRoleOpen ? 'rotate-180' : ''}
          `}
                    />
                  </div>
                </div>
              )} */}
            </button>

            {/* DROPDOWN (unchanged) */}
            {isRoleOpen && (
              <div className="absolute left-0 sm:left-auto sm:right-0 mt-3 w-64 max-w-[calc(100vw-1.5rem)] bg-white border rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                <div className="p-2 space-y-1">
                  {filteredRoles.map((role) => {
                    const Icon = role.icon;
                    const isActive = roleName === role.key;

                    return (
                      <button
                        key={role.key}
                        onClick={(e) => {
                          setIsRoleOpen(false);
                          handleUpdateSwtich(e, role.key, role.id);
                        }}
                        className={`
                w-full flex items-center justify-between
                px-3 py-3 rounded-xl text-sm transition-all duration-200
                ${
                  isActive
                    ? `${role.bg} ${role.color} font-medium`
                    : 'text-gray-700 hover:bg-gray-50'
                }
              `}
                      >
                        <div className="flex text-xs items-center gap-3">
                          <div className={`p-2 rounded-lg ${role.bg}`}>
                            <Icon className={`w-4 h-4 ${role.color}`} />
                          </div>
                          <span>{role.label}</span>
                        </div>

                        {isActive && (
                          <div className="w-2 h-2 rounded-full bg-[#375AE7]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {/* Notifications */}
          <div ref={notificationRef} className="relative shrink-0">
            {/* Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();

                if (!open) {
                  handleOpen();
                }

                setOpen(!open);
              }}
              className="relative h-11 w-11 lg:h-12 lg:w-12 flex items-center justify-center rounded-xl bg-white hover:shadow-md transition"
            >
              <img src="/lamp.svg" className="w-6 h-6" />

              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shadow">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Dropdown */}
            {open && (
              <div className="fixed sm:absolute left-3 right-3 sm:left-auto sm:right-0 top-[4.5rem] sm:top-auto mt-0 sm:mt-3 w-auto sm:w-[420px] max-w-[calc(100vw-1.5rem)] bg-white border border-gray-200 rounded-2xl shadow-xl z-[100] animate-in fade-in slide-in-from-top-2">
                {/* Header */}
                {showLoader && (
                  <div className="space-y-3 px-2 py-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-xl border bg-white animate-pulse"
                      >
                        <div className="flex justify-between">
                          <div className="w-full">
                            <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                            <div className="h-2 bg-gray-200 rounded w-3/4 mb-2"></div>
                            <div className="h-2 bg-gray-100 rounded w-1/4"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between items-center px-4 py-3 border-b bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
                  <h3 className="font-semibold text-gray-800">Notifications</h3>
                </div>
                {/* Empty State */}
                {notifications.length === 0 && (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    No notifications yet 🚀
                  </div>
                )}

                {/* List */}
                <div
                  onScroll={handleScroll}
                  className="max-h-[60vh] overflow-y-auto overscroll-contain px-3 pb-3 space-y-4 scroll-smooth"
                >
                  {/* NEW */}
                  {newItems.length > 0 && (
                    <div>
                      <div className="text-xs mt-2 font-semibold text-blue-600 px-1 mb-2">
                        New notifications
                      </div>

                      <div className="space-y-2">
                        {newItems.map((item) => (
                          <NotificationItem key={item.id} item={item} isNew />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* EARLIER */}
                  {oldItems.length > 0 && (
                    <div>
                      <div className="text-xs text-gray-400 px-1 mt-3 mb-2">
                        Earlier
                      </div>

                      <div className="space-y-2">
                        {oldItems.map((item) => (
                          <NotificationItem key={item.id} item={item} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsSupportOpen(true)}
            className={`flex items-center justify-center shrink-0 h-11 w-11 sm:w-auto gap-2 px-0 sm:px-3 py-2 text-xs rounded-xl transition-all duration-200 text-white shadow hover:scale-105`}
            style={{ background: '#375AE7' }}
            aria-label="Need Help?"
          >
            {/* Icon */}
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 13a3 3 0 01-3 3h-1v-4h1a3 3 0 013 3zM6 13a3 3 0 003 3h1v-4H9a3 3 0 00-3 3z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3a9 9 0 00-9 9v3a3 3 0 003 3h1v-6H6a6 6 0 1112 0h-1v6h1a3 3 0 003-3v-3a9 9 0 00-9-9z"
              />
            </svg>

            {/* Text */}
            <span className="hidden sm:inline">Need Help?</span>
          </button>
          <button
            onClick={() => handleChangeMenu()}
            className={`flex items-center justify-center shrink-0 h-11 w-11 sm:w-auto gap-2 px-0 sm:px-3 py-2 text-xs rounded-xl transition-all duration-200 text-white shadow hover:scale-105`}
            style={{ background: 'black' }}
            aria-label="Logout"
          >
            {/* Icon */}
            <img src="/logout-white.svg" alt="logout" className="w-5 h-5" />
            {/* Text */}
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
      <SessionWarningModal
        isOpen={isWarning}
        timeLeft={timeLeft}
        onStayConnected={stayConnected}
        onLogout={handleLogout}
      />
      {isSupportOpen && (
        <div className="fixed inset-0 z-[20] flex items-center justify-center bg-black/40 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto">
          {/* Modal */}
          <ContactUs onClose={() => setIsSupportOpen(false)} isLogin={true} />
        </div>
      )}
    </header>
  );
};

export default OnboardingHeader;
